/**
 * Chat History List Component
 *
 * Full page view for browsing and managing all chat sessions.
 * Accessed via /chat?history=true
 */

import * as React from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { formatDistanceToNow } from 'date-fns'
import {
  MessageSquare,
  Trash2,
  Pencil,
  MoreHorizontal,
  Plus,
  Search,
  Clock,
  Hash,
  Coins,
} from 'lucide-react'
import { useChatSessions, useDeleteChatSession, useUpdateChatSession } from '@/hooks/useChatSessions'
import { useCurrentUser } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'

export function ChatHistoryList() {
  const navigate = useNavigate()
  const [search, setSearch] = React.useState('')
  const [deleteSessionId, setDeleteSessionId] = React.useState<number | null>(null)
  const [editSession, setEditSession] = React.useState<{ id: number; title: string } | null>(null)
  const [editTitle, setEditTitle] = React.useState('')

  // Get current user for filtering sessions
  const { data: currentUser } = useCurrentUser()

  // Fetch sessions for current user only
  const { data, isPending, error } = useChatSessions({
    limit: 100,
    userId: currentUser?.id?.toString(),
  })
  const deleteSession = useDeleteChatSession()
  const updateSession = useUpdateChatSession()

  const sessions = data?.sessions ?? []

  // Filter sessions by search
  const filteredSessions = React.useMemo(() => {
    if (!search.trim()) return sessions
    const searchLower = search.toLowerCase()
    return sessions.filter(
      (s) =>
        (s.title?.toLowerCase() ?? '').includes(searchLower) ||
        s.id.toString().includes(searchLower)
    )
  }, [sessions, search])

  // Group sessions by date
  const groupedSessions = React.useMemo(() => {
    const groups: { label: string; sessions: typeof sessions }[] = []
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const lastWeek = new Date(today)
    lastWeek.setDate(lastWeek.getDate() - 7)
    const lastMonth = new Date(today)
    lastMonth.setMonth(lastMonth.getMonth() - 1)

    const todaySessions: typeof sessions = []
    const yesterdaySessions: typeof sessions = []
    const lastWeekSessions: typeof sessions = []
    const lastMonthSessions: typeof sessions = []
    const olderSessions: typeof sessions = []

    for (const session of filteredSessions) {
      const date = new Date(session.createdAt)
      date.setHours(0, 0, 0, 0)

      if (date.getTime() === today.getTime()) {
        todaySessions.push(session)
      } else if (date.getTime() === yesterday.getTime()) {
        yesterdaySessions.push(session)
      } else if (date >= lastWeek) {
        lastWeekSessions.push(session)
      } else if (date >= lastMonth) {
        lastMonthSessions.push(session)
      } else {
        olderSessions.push(session)
      }
    }

    if (todaySessions.length > 0) groups.push({ label: 'Today', sessions: todaySessions })
    if (yesterdaySessions.length > 0) groups.push({ label: 'Yesterday', sessions: yesterdaySessions })
    if (lastWeekSessions.length > 0) groups.push({ label: 'Last 7 Days', sessions: lastWeekSessions })
    if (lastMonthSessions.length > 0) groups.push({ label: 'Last 30 Days', sessions: lastMonthSessions })
    if (olderSessions.length > 0) groups.push({ label: 'Older', sessions: olderSessions })

    return groups
  }, [filteredSessions])

  // Handle delete
  const handleDelete = async () => {
    if (deleteSessionId) {
      await deleteSession.mutateAsync(deleteSessionId)
      setDeleteSessionId(null)
    }
  }

  // Handle rename
  const handleRename = async () => {
    if (editSession && editTitle.trim()) {
      await updateSession.mutateAsync({
        sessionId: editSession.id,
        title: editTitle.trim(),
      })
      setEditSession(null)
      setEditTitle('')
    }
  }

  // Start editing
  const startEdit = (session: { id: number; title: string | null }) => {
    setEditSession({ id: session.id, title: session.title || '' })
    setEditTitle(session.title || 'Untitled chat')
  }

  return (
    <div className="flex flex-col h-full bg-page">
      {/* Centered Container */}
      <div className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-foreground">Chat History</h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                {sessions.length} conversation{sessions.length !== 1 ? 's' : ''} with Nurin AI
              </p>
            </div>
            <Link to="/chat" search={{ new: 'true' }}>
              <Button className="gap-2 rounded-full px-4 sm:px-5 h-9 sm:h-10 text-sm">
                <Plus className="h-4 w-4" />
                New Chat
              </Button>
            </Link>
          </div>

          {/* Search */}
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search conversations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 sm:pl-11 h-10 sm:h-11 rounded-full bg-card border-border/50"
            />
          </div>
        </div>

        {/* Session List */}
        <div className="pb-8">
        {isPending ? (
          <div className="flex items-center justify-center h-60">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin h-8 w-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
              <p className="text-sm text-muted-foreground">Loading conversations...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="h-8 w-8 text-red-500" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">Failed to load</h3>
            <p className="text-sm text-muted-foreground">Please try again later</p>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="h-8 w-8 text-emerald-500" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              {search ? 'No matching conversations' : 'No conversations yet'}
            </h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              {search
                ? 'Try a different search term'
                : 'Start chatting with Nurin AI to explore your healthcare data'}
            </p>
            {!search && (
              <Link to="/chat" search={{ new: 'true' }}>
                <Button className="rounded-full px-6">Start New Chat</Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {groupedSessions.map((group) => (
              <div key={group.label}>
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-1">
                  {group.label}
                </h2>
                <div className="space-y-2">
                  {group.sessions.map((session) => (
                    <div
                      key={session.id}
                      className="group flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-border/50 bg-card hover:bg-hover hover:border-border transition-all duration-200"
                    >
                      {/* Icon */}
                      <div className="flex-shrink-0 w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 flex items-center justify-center">
                        <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500" />
                      </div>

                      {/* Content - Clickable area */}
                      {editSession?.id === session.id ? (
                        <div className="flex-1 min-w-0">
                          <Input
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            placeholder="Conversation title"
                            className="h-8 rounded-lg text-[15px] font-medium"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleRename()
                              } else if (e.key === 'Escape') {
                                setEditSession(null)
                                setEditTitle('')
                              }
                            }}
                            onBlur={handleRename}
                          />
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1.5">
                            <span className="flex items-center gap-1.5">
                              <Clock className="h-3 w-3" />
                              {formatDistanceToNow(new Date(session.createdAt), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <Link
                          to="/chat"
                          search={{ session: session.id.toString() }}
                          className="flex-1 min-w-0"
                        >
                          <h3
                            className="font-medium text-foreground truncate text-[15px]"
                            onDoubleClick={(e) => {
                              e.preventDefault()
                              startEdit(session)
                            }}
                            title="Double-click to rename"
                          >
                            {session.title || 'Untitled chat'}
                          </h3>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1.5">
                            <span className="flex items-center gap-1.5">
                              <Clock className="h-3 w-3" />
                              {formatDistanceToNow(new Date(session.createdAt), { addSuffix: true })}
                            </span>
                            {session.messageCount !== null && session.messageCount > 0 && (
                              <span className="flex items-center gap-1">
                                <Hash className="h-3 w-3" />
                                {session.messageCount}
                              </span>
                            )}
                            {session.totalTokens !== null && session.totalTokens > 0 && (
                              <span className="flex items-center gap-1">
                                <Coins className="h-3 w-3" />
                                {(session.totalTokens / 1000).toFixed(1)}k
                              </span>
                            )}
                            {session.branch && (
                              <span className="px-2 py-0.5 rounded-full bg-muted/50 text-[11px] font-medium">
                                {session.branch.shortName}
                              </span>
                            )}
                          </div>
                        </Link>
                      )}

                      {/* Actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-36">
                          <DropdownMenuItem onClick={() => startEdit(session)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeleteSessionId(session.id)}
                            className="text-red-500 focus:text-red-500"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteSessionId !== null} onOpenChange={() => setDeleteSessionId(null)}>
        <AlertDialogContent className="max-w-md rounded-2xl">
          <AlertDialogHeader>
            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-2">
              <Trash2 className="h-6 w-6 text-red-500" />
            </div>
            <AlertDialogTitle className="text-center">Delete conversation?</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              This will permanently delete this conversation and all its messages.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center gap-3 mt-2">
            <AlertDialogCancel className="rounded-full px-6">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 rounded-full px-6"
            >
              {deleteSession.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rename Dialog */}
      <AlertDialog open={editSession !== null} onOpenChange={() => setEditSession(null)}>
        <AlertDialogContent className="max-w-md rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Rename conversation</AlertDialogTitle>
            <AlertDialogDescription>
              Give this conversation a memorable name.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Conversation title"
              className="h-11 rounded-xl"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRename()
                }
              }}
            />
          </div>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel className="rounded-full px-6">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRename} className="rounded-full px-6">
              {updateSession.isPending ? 'Saving...' : 'Save'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
